// Graph Imports
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import {
  changeWebApplicationOwner,
  createUser,
  createWebApplication,
  createWebApplicationTestUser,
  deleteWebApplication,
  deleteWebApplicationTestUser,
  scheduleTest as scheduleTestMutation,
  setSecurityHeader,
  startTest,
  stopTest,
  updateWebApplicationDomains as updateWebApplicationDomainsMutation,
  updateWebApplicationReduced,
  updateWebApplicationTestUser,
  verifyWebAppOwnership,
  welcomeNewUser,
} from "./graphql/mutations";
import {
  getTestResults,
  getTestStatusSnapshot,
  getVulnerabilities,
  getVulnHistory,
  listLatestTestsForDashboard,
  listLiveFindingsForTest,
  listVulnHistories,
  listWebAppTestStatuses,
  getWebApplicationReduced,
  getWebAppTestStatus,
  listUsers,
  quickListTestResultssReduced,
  quickListVulnerabilitiess,
  quickListWebApplicationsSuperReduced,
} from "./graphql/queries";

import {
  constructNonEmptyGraphInputs,
  convertFromUIToGraphAPI,
  convertListFromUIToGraphAPI,
  getGraphData,
} from "@/utils/transform";

// Class import
import UserClass from "../models/user";
import VulnerabilityClass from "../models/vulnerability";
import WebApplicationClass from "../models/webApplication";
import { WebAppOperationsClass } from "../models/webAppOperations";

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

/**
 * Safe wrapper around API.graphql that catches errors and returns null.
 * Logs the error with context so failures are diagnosable without crashing the UI.
 */
async function safeGraphqlCall(
  operation: any,
  operationName: string
): Promise<GraphQLResponse | null> {
  try {
    return (await API.graphql(operation)) as GraphQLResponse;
  } catch (graphqlError: any) {
    const message = graphqlError?.errors?.[0]?.message || graphqlError?.message || "Unknown error";
    console.error(`GraphQL error in ${operationName}: ${message}`);
    return null;
  }
}

interface GraphQLResponse {
  data: any;
  [key: string]: any;
}

const DEFAULT_PAGE_LIMIT = 100;

/**
 * Fetches all pages of a paginated GraphQL query by following nextToken.
 * Use for queries that return { items: [...], nextToken: "..." }.
 */
async function fetchAllPages(
  query: string,
  variables: Record<string, any>,
  dataKey: string
): Promise<any[]> {
  const allItems: any[] = [];
  let nextToken: string | null = null;

  do {
    const queryVariables = { ...variables, limit: DEFAULT_PAGE_LIMIT, nextToken };
    const response = await safeGraphqlCall(
      graphqlOperation(query, queryVariables),
      `fetchAllPages(${dataKey})`
    );
    if (!response) break;
    const data = getGraphData(response, dataKey);

    if (data?.items) {
      allItems.push(...data.items);
    }
    nextToken = data?.nextToken ?? null;
  } while (nextToken);

  return allItems;
}


interface AppIdAndName {
  id: string;
  name: string;
  [key: string]: any;
}

// Get Pentester Leaderboard
// Get List of Web Apps
// ---------------------
//
export function getListOfWebapps(
  listOfAppIdsAndNames: AppIdAndName[],
  subscriptionClient: any
): WebApplicationClass[] {
  // Get the list of web applications
  const webapps = listOfAppIdsAndNames?.map((v) => {
    const webapp = new WebApplicationClass();
    // setWebApplication is async — catch its rejection so "No current user"
    // errors during signout don't become unhandled Promise rejections.
    webapp.setWebApplication(v as any, subscriptionClient, false).catch(() => {});
    return webapp;
  });

  return webapps;
}

// Get Web Application
// --------------------
//
export async function getWebApplication(id: string): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(getWebApplicationReduced, { id: id }),
    "getWebApplication"
  );
  return response ? getGraphData(response, "getWebApplication") : null;
}

// Get Web Application
// --------------------
//
export function getSingleWebApplication(
  id: string,
  subscriptionClient: any
): WebApplicationClass {
  const webapp = new WebApplicationClass();
  getWebApplication(id)
    .then((data) => {
      webapp.setWebApplication(data, subscriptionClient).catch(() => {});
    })
    .catch(() => {});
  return webapp;
}

// Get List of Web Apps
// ---------------------
//
export async function getMinimalListOfWebapps(): Promise<any[]> {
  const items = await fetchAllPages(
    quickListWebApplicationsSuperReduced,
    {},
    "quickListWebApplications"
  );

  if (items.length > 0) {
    const webappOps = new WebAppOperationsClass();
    return webappOps.sortByWebappName(items);
  }

  return [];
}

// Get List of Web App Names
// --------------------------
// Returns list of lowercase web app names
export async function getListOfAppNames(): Promise<string[]> {
  const listOfNames: string[] = [];
  const data = await getMinimalListOfWebapps();
  for (let i = 0; i < data.length; i++) {
    listOfNames.push(data[i].lowercasename);
  }
  return listOfNames;
}

// Get List of Web App URLs
// -------------------------
// Returns list of registered URLs for duplicate-URL detection in the Add Application wizard.
export async function getListOfAppUrls(): Promise<string[]> {
  const data = await getMinimalListOfWebapps();
  return data.map((item: any) => item.url).filter(Boolean);
}

// Get List of Test Results
// -------------------------
//
export async function getReducedListOfTestResults(
  webappid: string
): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(quickListTestResultssReduced, { webappid: webappid }),
    "getReducedListOfTestResults"
  );
  return response ? getGraphData(response, "quickListTestResultss") : null;
}

// Fetch a slim snapshot of a single test's current status — used by
// the WebApplicationClass in-flight resync paths (periodic poll +
// visibility-change refetch) to catch up when the onTestStatusChange
// subscription has missed events (WebSocket drop, idle timeout). The
// projection mirrors the subscription's onTestStatusChange payload so
// the result can be fed straight through handleTestStatusEvent —
// keeping the resync write-path identical to the live-event
// write-path, no second code path to drift.
export async function fetchTestStatusSnapshot(testid: string): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(getTestStatusSnapshot, { id: testid }),
    "fetchTestStatusSnapshot"
  );
  return response ? getGraphData(response, "getTestResults") : null;
}

// One row of the slim findings projection — the shape returned by
// listLiveFindingsForTest, and (bar dateLastSeen, which the event type
// doesn't carry) the shape the onVulnerabilityDiscovered subscription
// publishes. Every field is optional because both sources are
// projections: the consumer defaults what's missing.
export interface SlimFindingRow {
  id?: string;
  reference?: string;
  riskRating?: string;
  vulnName?: string;
  vulnCategory?: string;
  vulnLocation?: string;
  pageUrl?: string;
  title?: string;
  dateFirstFound?: number;
  dateLastSeen?: number;
}

// Fetch Live Findings Snapshot
// -----------------------------
// Every finding the backend has recorded for one test, in the slim
// projection. The reconcile path for an in-flight run: the discovery
// subscription is best-effort (AppSync has no replay, so a dropped
// socket loses every finding published while it was down), and this is
// how the FE catches up without paying for screenshots.
export async function fetchLiveFindingsSnapshot(
  testid: string
): Promise<SlimFindingRow[]> {
  return fetchAllPages(
    listLiveFindingsForTest,
    { testid },
    "quickListVulnerabilitiess"
  );
}

// Fetch Vulnerability By Id
// --------------------------
// One vulnerability, in full. Backed by a single DynamoDB GetItem
// (Query.getVulnerabilities.js, keyed on the tenantId from the caller's
// JWT), so it answers in a fraction of the time a whole-test hydrate
// takes — getTestResults returns EVERY vuln in the run along with all of
// their sub-findings and captured HTTP request/response bodies.
//
// That difference is the point: opening one finding should cost one
// record, not the entire test.
export async function fetchVulnerabilityById(vulnid: string): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(getVulnerabilities, { id: vulnid }),
    "fetchVulnerabilityById"
  );
  return response ? getGraphData(response, "getVulnerabilities") : null;
}

// Get List of Vulns
// ------------------
//
export async function getListOfVulns(
  testresultid: string
): Promise<VulnerabilityClass[]> {
  const items = await fetchAllPages(
    quickListVulnerabilitiess,
    { testid: testresultid },
    "quickListVulnerabilitiess"
  );

  return items.map((item) => {
    const vuln = new VulnerabilityClass();
    vuln.setVulnerability(item);
    return vuln;
  });
}

// Get List of Users
// ------------------
//
export async function getListOfUsers(user: UserClass): Promise<UserClass[]> {
  const listOfUsers: UserClass[] = [];

  if (user && user.isUserAdmin()) {
    const data = await getListOfUsersFromAPI();
    if (data) {
      const items = data["items"];
      for (let i = 0; i < items.length; i++) {
        const newUser = new UserClass();
        newUser.setUser(items[i]);
        listOfUsers.push(newUser);
      }
    }
  }
  return listOfUsers;
}

// Get List of Users from API
// ---------------------------
//
export async function getListOfUsersFromAPI(): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(listUsers, { role: "" }),
    "getListOfUsersFromAPI"
  );
  return response ? getGraphData(response, "listUsers") : null;
}

// Change Web App Owner
// --------------------
//
export async function changeWebAppOwner(
  webappid: string,
  newOwnerUsername: string
): Promise<any> {
  const inputDict = {
    id: webappid,
    newOwner: newOwnerUsername,
  };

  const response = await safeGraphqlCall(
    graphqlOperation(changeWebApplicationOwner, {
      input: constructNonEmptyGraphInputs(inputDict),
    }),
    "changeWebAppOwner"
  );
  return response ? getGraphData(response, "changeWebApplicationOwner") : null;
}

// Delete a web app
export async function deleteWebapp(webappid: string): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(deleteWebApplication, { input: { id: webappid } }),
    "deleteWebapp"
  );
  return response ? getGraphData(response, "deleteWebApplication") : null;
}

// Create Web App
//
// One-shot creation: every webapp setting the wizard collects (RPS,
// security header, test accounts, link scope) goes in this single
// mutation. The BE's CreateWebApplicationInput accepts them inline
// and persists them in the same transaction, replacing the old
// "create webapp → updateAppSettings → saveTestAccounts →
// setSecurityHeader" chain that the FE used to fire.
//
// abbrev and color are derived on the frontend from the app name and
// held only in memory — they are NOT part of the create input and are
// not persisted to the backend by any mutation. Because they aren't
// stored, a reloaded webapp comes back with color/abbrev undefined and
// each render re-derives them via `color || deriveAppColor(name)`. Keep
// the in-memory default falsy (see WebApplicationClass constructor) so
// that same fallback fires for a freshly created app too.
//
// Server-side guards worth knowing about (BE brief):
//   * maxRequestsPerSecond is clamped to [10, 100]. null/0/negative
//     becomes 10; >100 becomes 100. FE picker already restricts to
//     the same range — clamp is a guard, not a UX primitive.
//   * securityHeaderValue without a securityHeaderName is silently
//     ignored. The wizard enforces "name required if value" before
//     calling here.
//   * testAccounts has a 2-account cap. Sending more returns an
//     authorization-style error.
//   * Reject-whole semantics on testAccounts: if any single account
//     fails to create (DDB throttle, SSM error, etc.), the call
//     returns failure but the webapp row stays created. Caller can
//     either delete the webapp + retry or top up via
//     createWebApplicationTestUser. See NewApplication wizard for
//     the recovery path.
export interface CreateWebAppTestAccountInput {
  username: string;
  password?: string | null;
  pin?: string | null;
  totpSecret?: string | null;
  userPriority: number;
}

export async function createWebApp(
  name: string,
  url: string,
  featureTeamName: string,
  frontEndFramework: string,
  backEndFramework: string,
  dataType: string[],
  userType: string[],
  testAcknowledged: boolean,
  maxRequestsPerSecond: number | null,
  securityHeaderName: string | null,
  securityHeaderValue: string | null,
  testAccounts: CreateWebAppTestAccountInput[],
  allowedLinks: string[] | null,
  deniedLinks: string[] | null,
  apiDomains: string[] | null,
  // Optional purchased license ("entitlement") to bind this app to. Null lets
  // the BE pick the first free slot (or pass through for a legacy/unbilled
  // tenant). The BE rejects the create if the tenant has licenses but none free.
  entitlementId: string | null = null,
): Promise<any> {
  // Strip empty / whitespace-only entries from link lists before
  // sending. The wizard builds these by splitting a free-text input
  // on /[\s,;:]+/, which returns [""] for an empty field — leaking
  // a phantom "empty allowed path" to the BE. Filter here so every
  // caller benefits, not just the create-app wizard.
  const cleanedAllowedLinks = (allowedLinks ?? []).filter(
    (link) => link.trim().length > 0,
  );
  const cleanedDeniedLinks = (deniedLinks ?? []).filter(
    (link) => link.trim().length > 0,
  );
  // API domains follow the same shape as the link lists (a list of
  // strings). Same empty-entry strip so a blank input doesn't send [""].
  const cleanedApiDomains = (apiDomains ?? []).filter(
    (domain) => domain.trim().length > 0,
  );

  const inputDict = {
    name: name,
    url: url,
    allowedLinks: cleanedAllowedLinks.length > 0 ? cleanedAllowedLinks : null,
    deniedLinks: cleanedDeniedLinks.length > 0 ? cleanedDeniedLinks : null,
    apiDomains: cleanedApiDomains.length > 0 ? cleanedApiDomains : null,
    featureTeamName: featureTeamName,
    frontEndFramework: frontEndFramework,
    backEndFramework: backEndFramework,
    dataType: convertListFromUIToGraphAPI(dataType),
    userType: convertListFromUIToGraphAPI(userType),
    testAcknowledged: testAcknowledged,
    maxRequestsPerSecond:
      maxRequestsPerSecond && maxRequestsPerSecond > 0 ? maxRequestsPerSecond : null,
    securityHeaderName: securityHeaderName || null,
    // Only forward the value when a name is present. Value-without-
    // name is dropped server-side, so guarding here avoids logging
    // a phantom secret on a request that would have been ignored.
    securityHeaderValue:
      securityHeaderName && securityHeaderValue ? securityHeaderValue : null,
    testAccounts:
      testAccounts.length > 0
        ? testAccounts.map((account) => ({
            username: account.username,
            password: account.password || null,
            pin: account.pin || null,
            totpSecret: account.totpSecret || null,
            userPriority: account.userPriority,
          }))
        : null,
    entitlementId: entitlementId || null,
  };

  // Bypass safeGraphqlCall here so any AppSync rejection (BE
  // validation, auth, schema mismatch, partial-create failure on
  // test accounts, …) propagates to the wizard with its real
  // message. The wizard surfaces it on the Application Settings
  // step so the user can fix and retry. safeGraphqlCall is fine
  // for fire-and-forget mutations where there's no recovery
  // affordance, but for the bundled create — which represents an
  // entire wizard's worth of user input — silent failure is worse
  // than a visible error message.
  try {
    const response = (await API.graphql(
      graphqlOperation(createWebApplication, {
        input: constructNonEmptyGraphInputs(inputDict),
      })
    )) as GraphQLResponse;
    return getGraphData(response, "createWebApplication");
  } catch (graphqlError: any) {
    const message =
      graphqlError?.errors?.[0]?.message ||
      graphqlError?.message ||
      "Failed to create application";
    console.error("GraphQL error in createWebApp:", message, graphqlError);
    throw new Error(message);
  }
}

// Update Web App Properties
export async function updateAppProperties(
  id: string,
  featureTeamName: string,
  frontEndFramework: string,
  backEndFramework: string,
  dataType: string[],
  userType: string[]
): Promise<any> {
  const inputDict = {
    id: id,
    featureTeamName: featureTeamName,
    frontEndFramework: frontEndFramework,
    backEndFramework: backEndFramework,
    dataType: convertListFromUIToGraphAPI(dataType),
    userType: convertListFromUIToGraphAPI(userType),
  };

  const response = await safeGraphqlCall(
    graphqlOperation(updateWebApplicationReduced, {
      input: constructNonEmptyGraphInputs(inputDict),
    }),
    "updateAppProperties"
  );
  return response ? getGraphData(response, "updateWebApplication") : null;
}

// Update Web App Domains (pre-verification re-scope)
// ---------------------------------------------------
// Re-sets url + apiDomains on an UNVERIFIED web app in place. The backend
// enforces the security gates (Admin/Owner-only, ownershipVerified == false,
// not deleted, no terminal test) and re-canonicalises the input, so this
// forwards the raw values and lets the server be authoritative.
//
// Bypasses safeGraphqlCall so a backend rejection (invalid scope path, unsafe
// URL, duplicate URL, already-verified lock) propagates with its real message
// for the Settings modal to surface — mirrors createWebApp, where silent
// failure would be worse than a visible, actionable error.
export async function updateWebApplicationDomains(
  id: string,
  url: string,
  apiDomains: string[] | null,
): Promise<any> {
  // Strip empty / whitespace-only entries so a blank input doesn't send [""].
  // An empty list is sent as null → the backend treats the app as having no
  // API domains (removal path), same contract as createWebApp.
  const cleanedApiDomains = (apiDomains ?? []).filter(
    (domain) => domain.trim().length > 0,
  );

  const inputDict = {
    id: id,
    url: url,
    apiDomains: cleanedApiDomains.length > 0 ? cleanedApiDomains : null,
  };

  try {
    const response = (await API.graphql(
      graphqlOperation(updateWebApplicationDomainsMutation, {
        input: constructNonEmptyGraphInputs(inputDict),
      })
    )) as GraphQLResponse;
    return getGraphData(response, "updateWebApplicationDomains");
  } catch (graphqlError: any) {
    const message =
      graphqlError?.errors?.[0]?.message ||
      graphqlError?.message ||
      "Failed to update domains";
    console.error("GraphQL error in updateWebApplicationDomains:", message, graphqlError);
    throw new Error(message);
  }
}

// Update Specific Web App Settings
export async function updateAppSettings(
  id: string,
  allowedLinks: string[],
  deniedLinks: string[],
  maxRequestsPerSecond: number
): Promise<any> {
  const inputDict = {
    id: id,
    maxRequestsPerSecond: maxRequestsPerSecond,
    allowedLinks: allowedLinks,
    deniedLinks: deniedLinks,
  };

  const response = await safeGraphqlCall(
    graphqlOperation(updateWebApplicationReduced, {
      input: constructNonEmptyGraphInputs(inputDict),
    }),
    "updateAppSettings"
  );
  return response ? getGraphData(response, "updateWebApplication") : null;
}

// Schedule Test
// --------------
//
export async function scheduleTest(
  id: string,
  dateToTest: number,
  testFrequency: string
): Promise<any> {
  const inputDict = {
    id: id,
    dateToTest: dateToTest,
    testFrequency: convertFromUIToGraphAPI(testFrequency),
  };

  // Routes to the schedule_test Lambda handler which both saves the schedule
  // to DynamoDB and creates the EventBridge rule + SQS target.
  const response = await safeGraphqlCall(
    graphqlOperation(scheduleTestMutation, { input: inputDict }),
    "scheduleTest"
  );
  return response ? getGraphData(response, "scheduleTest") : null;
}

// Start Test
export interface StartTestResult {
  testid: string | null;
  message: string | null;
  success: boolean;
  retestExpiryDays: number | null;
  retestExpiresAt: number | null;
}

export async function startWebappTest(
  webappid: string,
  retestOnly: boolean = false
): Promise<StartTestResult> {
  const inputDict: Record<string, any> = {
    id: webappid,
  };
  if (retestOnly) {
    inputDict.retestOnly = true;
  }

  try {
    const response = (await API.graphql(
      graphqlOperation(startTest, {
        input: constructNonEmptyGraphInputs(inputDict),
      })
    )) as GraphQLResponse;
    const result = getGraphData(response, "startTest");
    return {
      testid: result?.testid ?? null,
      message: result?.message ?? null,
      success: result?.success ?? false,
      retestExpiryDays: result?.retestExpiryDays ?? null,
      retestExpiresAt: result?.retestExpiresAt ?? null,
    };
  } catch (error: any) {
    // GraphQL errors come back as { errors: [{ message: "..." }] }
    const errorMessage = error?.errors?.[0]?.message || "Failed to start test";
    console.error("Failed to start test:", error);
    return { testid: null, message: errorMessage, success: false, retestExpiryDays: null, retestExpiresAt: null };
  }
}

// Stop Test
export interface StopTestResult {
  testid: string | null;
  message: string | null;
  success: boolean;
}

export async function stopWebappTest(webappid: string): Promise<StopTestResult> {
  try {
    const response = (await API.graphql(
      graphqlOperation(stopTest, {
        input: constructNonEmptyGraphInputs({ id: webappid }),
      })
    )) as GraphQLResponse;
    const result = getGraphData(response, "stopTest");
    return {
      testid: result?.testid ?? null,
      message: result?.message ?? null,
      success: result?.success ?? false,
    };
  } catch (error: any) {
    // Backend rejects start/stop during transition phases (Provisioning,
    // Stopping). The GraphQL error shape is { errors: [{ message: "..." }] };
    // surface the message verbatim so the caller can show it to the user.
    const errorMessage = error?.errors?.[0]?.message || "Failed to stop test";
    console.error("Failed to stop test:", error);
    return { testid: null, message: errorMessage, success: false };
  }
}

// Delete Test Accounts
export async function deleteOldTestAccounts(
  testAccountId: string
): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(deleteWebApplicationTestUser, {
      input: { id: testAccountId },
    }),
    "deleteOldTestAccounts"
  );
  return response ? getGraphData(response, "deleteWebApplicationTestUser") : null;
}

// Update Test Account
export async function updateTestAccount(
  testAccountId: string,
  testUser: { password: string | null; pin: string | null; totpSecret?: string | null; userPriority?: number | null; username: string | null }
): Promise<any> {
  const inputDict = {
    id: testAccountId,
    password: testUser.password,
    pin: testUser.pin,
    totpSecret: testUser.totpSecret ?? null,
    userPriority: testUser.userPriority ?? 0,
    username: testUser.username,
  };
  const response = await safeGraphqlCall(
    graphqlOperation(updateWebApplicationTestUser, {
      input: constructNonEmptyGraphInputs(inputDict),
    }),
    "updateTestAccount"
  );
  return response ? getGraphData(response, "updateWebApplicationTestUser") : null;
}

// Add Test Account
export async function addTestAccount(
  webappid: string,
  testUser: { password: string | null; pin: string | null; totpSecret?: string | null; userPriority?: number | null; username: string | null }
): Promise<any> {
  const inputDict = {
    webappid: webappid,
    password: testUser.password,
    pin: testUser.pin,
    totpSecret: testUser.totpSecret ?? null,
    userPriority: testUser.userPriority ? testUser.userPriority : 0,
    username: testUser.username,
  };

  const response = await safeGraphqlCall(
    graphqlOperation(createWebApplicationTestUser, {
      input: constructNonEmptyGraphInputs(inputDict),
    }),
    "addTestAccount"
  );
  return response ? getGraphData(response, "createWebApplicationTestUser") : null;
}

// Get Web Application Test Status
export async function getWebAppTestStatusFromGraph(
  webappid: string
): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(getWebAppTestStatus, { id: webappid }),
    "getWebAppTestStatusFromGraph"
  );
  return response ? getGraphData(response, "getWebAppTestStatus") : null;
}

export async function getFullTestResultFromGraph(testid: string): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(getTestResults, { id: testid }),
    "getFullTestResultFromGraph"
  );
  return response ? getGraphData(response, "getTestResults") : null;
}

export async function verifyAppOwnership(webappid: string): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(verifyWebAppOwnership, { input: { id: webappid } }),
    "verifyAppOwnership"
  );
  return response ? getGraphData(response, "verifyWebAppOwnership") : null;
}

// Persist the preferred username chosen during account activation. Calls
// welcomeNewUser, which sets the caller's preferred_username in Cognito after
// validating uniqueness. Requires an established session, so callers must
// invoke it only after the activated user is fully signed in. Returns false on
// failure (e.g. the chosen name is taken) — non-fatal to the login flow.
export async function persistPreferredUsername(displayName: string): Promise<boolean> {
  const trimmed = displayName?.trim();
  if (!trimmed) return false;
  const response = await safeGraphqlCall(
    graphqlOperation(welcomeNewUser, { input: { username: trimmed } }),
    "persistPreferredUsername"
  );
  return !!(response && getGraphData(response, "welcomeNewUser"));
}

// Create User
export interface CreateUserResult {
  success: boolean;
  message: string | null;
}

// Bypasses safeGraphqlCall so the BE's seat-limit rejection (errorType
// "SeatLimitReached") and any other AppSync error propagate here with
// their real message. The Users page disables the invite button at the
// seat cap, but this is the backstop for a race (two admins inviting at
// once) — the caller surfaces message verbatim. Mirrors startWebappTest.
export async function createNewUser(
  givenname: string,
  familyname: string,
  emailaddress: string,
  role: string
): Promise<CreateUserResult> {
  const inputDict = {
    givenName: givenname,
    familyName: familyname,
    emailAddress: emailaddress,
    role: role,
  };

  try {
    const response = (await API.graphql(
      graphqlOperation(createUser, {
        input: constructNonEmptyGraphInputs(inputDict),
      })
    )) as GraphQLResponse;
    const created = getGraphData(response, "createUser");
    return { success: !!created, message: null };
  } catch (error: any) {
    // GraphQL errors come back as { errors: [{ message: "..." }] }.
    const errorMessage = error?.errors?.[0]?.message || "Failed to create user";
    console.error("Failed to create user:", error);
    return { success: false, message: errorMessage };
  }
}

export async function getVulnHistoryFromGraph(webappid: string): Promise<any> {
  const response = await safeGraphqlCall(
    graphqlOperation(getVulnHistory, { id: webappid }),
    "getVulnHistoryFromGraph"
  );
  return response ? getGraphData(response, "getVulnHistory") : null;
}

export async function listVulnHistoriesFromGraph(): Promise<any[]> {
  const response = await safeGraphqlCall(
    graphqlOperation(listVulnHistories),
    "listVulnHistoriesFromGraph"
  );
  return response ? (getGraphData(response, "listVulnHistories") || []) : [];
}

export async function listWebAppTestStatusesFromGraph(): Promise<any[]> {
  const response = await safeGraphqlCall(
    graphqlOperation(listWebAppTestStatuses),
    "listWebAppTestStatusesFromGraph"
  );
  return response ? (getGraphData(response, "listWebAppTestStatuses") || []) : [];
}

export async function listLatestTestsForDashboardFromGraph(): Promise<any[]> {
  const response = await safeGraphqlCall(
    graphqlOperation(listLatestTestsForDashboard),
    "listLatestTestsForDashboardFromGraph"
  );
  return response ? (getGraphData(response, "listLatestTestsForDashboard") || []) : [];
}

// Set Security Header
// --------------------
// Sets or clears a security header for a web application.
// Pass null for both name and value to clear the header.
//
export async function setSecurityHeaderForWebapp(
  webappId: string,
  securityHeaderName: string | null,
  securityHeaderValue: string | null
): Promise<any> {
  const inputDict: Record<string, any> = { webappId };
  if (securityHeaderName !== null) inputDict.securityHeaderName = securityHeaderName;
  if (securityHeaderValue !== null) inputDict.securityHeaderValue = securityHeaderValue;

  const response = await safeGraphqlCall(
    graphqlOperation(setSecurityHeader, { input: inputDict }),
    "setSecurityHeaderForWebapp"
  );
  return response ? getGraphData(response, "setSecurityHeader") : null;
}
